import React, { useRef, useState } from "react";
import { LoadIndicator } from 'devextreme-react/load-indicator';
import DrawerFooter from "cui/components/rightSideDrawer/DrawerFooter";
import { showErrorSnackbar, showSuccessSnackbar } from "cui/components/snackbar/Snackbar.slice";
import {
    Form, 
    Item,
    Label
} from "devextreme-react/form";
import { useInsightApi } from "hooks/use-insight-api";
import { useEffectSingleCalls } from "hooks/useEffectSingleCalls";
import { useAppDispatch, useAppSelector } from "store/app/hooks";
import { RootState } from "store/app/store";
import './applications.scss';
import { RegisteredApplicationDto } from '../../../api/NSwagApi/api';
import { refreshApplications } from '../../../store/app/applications.slice';
import { handleImageExtensionValidation } from './application.validation';
import ErrorMessage from 'cui/components/error/DisplayError';
import { GetApiErrorCode } from "constants/ErrorCodes";

type applicationEditProps = {
    onCancelClick: any;
    initialData: RegisteredApplicationDto;
};

type FormData = {
    applicationId: string;
    applicationName: string;
    adminFeature: string,
    viewFeature: string,
    executiveFeature: string,
    isActive: boolean;
    file: File;
}

export const ApplicationEdit: React.FunctionComponent<applicationEditProps> = (
    props
) => {
    const dispatch = useAppDispatch();
    const { getRegisteredApplicationById, editRegisteredApplication } = useInsightApi();
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const { initialData } = props;
    const [formData, setFormData] = useState<FormData>({} as FormData);
    const formRef = useRef<Form>(null);
    const logoRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<any>({});
    const [fileName, setFileName] = useState("");
    const [isDisplayNameEmpty, setIsDisplayNameEmpty] = useState<boolean>(false);
	const [isDisplayNameValid, setIsDisplayNameValid] = useState<boolean>(true);
	const [isAdminFeatureEmpty, setIsAdminFeatureEmpty] = useState<boolean>(false);
	const [isViewApplicationFeatureEmpty, setIsViewApplicationFeatureEmpty] = useState<boolean>(false);
    const [hasEnteredExecutiveReportsFeature, setHasEnteredExecutiveReportsFeature] = useState<boolean>(false);
    const [isValidFileExtension, setIsValidFileExtension] = useState<boolean>(true);
    const [currentlySubmitting, setCurrentlySubmitting] = useState<boolean>(false);

    const cemFeatures = useAppSelector(
        (state: RootState) => state.cem.roleFeatures
    )

    const sortCemFeatures = () => {
        const cemFeaturesCopy = [...cemFeatures]
        return cemFeaturesCopy.sort((a,b) => (a.name || '').localeCompare(b.name || ''))
    }

    const applicationDisplayNameOptions = {
        value: formData.applicationName,  
		onValueChanged: (e: { value: string; }) => validateDisplayName(e.value)
    }
    const featuresCreationAdminOptions = {
        displayExpr: 'name',
        items: sortCemFeatures(),
        value: formData.adminFeature,
        valueExpr: 'name',
        searchEnabled: true,
		onValueChanged: (e: { value: string; }) => validateAdminFeature(e.value)
    };
    const featuresCreationViewingOptions = {
        displayExpr: 'name',
        items: sortCemFeatures(),
        value: formData.viewFeature,
        valueExpr: 'name',
        searchEnabled: true,
		onValueChanged: (e: { value: string; }) => validateViewApplicationFeature(e.value)
    };
    const featuresCreationExecutiveOptions = {
        displayExpr: 'name',
        items: sortCemFeatures(),
        value: formData.executiveFeature,
        valueExpr: 'name',
        searchEnabled: true,
		onValueChanged: (e: { value: string; }) => validateExecutiveReportsFeature(e.value)
    };
    const visibilityEditorOptions = {
        value: formData.isActive
    };

    const validateDisplayName = (displayName: string) => {
		const pattern = /^[A-Za-z0-9\s\-()_]{1,100}$/;
		if (!displayName) {
		  setIsDisplayNameEmpty(true);
		  setIsDisplayNameValid(true);
		  return;
		}
		setIsDisplayNameEmpty(false);
		setIsDisplayNameValid(pattern.test(displayName));
	};

	const validateAdminFeature = (adminFeature: string) => {
		if(!adminFeature){
		  setIsAdminFeatureEmpty(true);
		  return;
		}
		setIsAdminFeatureEmpty(false);
	};

	const validateViewApplicationFeature = (viewApplicationFeature: string) => {
		if(!viewApplicationFeature){
		  setIsViewApplicationFeatureEmpty(true);
		  return;
		}
		setIsViewApplicationFeatureEmpty(false);
	};

    const validateExecutiveReportsFeature = (executiveReportsFeature: string) => {
		if(executiveReportsFeature){
			setHasEnteredExecutiveReportsFeature(true);
		}
		else {
			setHasEnteredExecutiveReportsFeature(false);
		}
	};

    const fetchData = async () => {
        const applicationId = initialData.applicationId;
        try {
            if (applicationId !== null && applicationId !== undefined) {
                setLoadingData(true);
                const data = await getRegisteredApplicationById(applicationId);
                if(data) {
                    if(data.features?.length === 2) {
                        setFormData({
                            adminFeature: data.features?.find(x=> x.featureTypeId === 0)!.feature,
                            viewFeature: data.features?.find(x=> x.featureTypeId === 1)!.feature,
                            executiveFeature: data.features?.find(x => x.featureTypeId === 2)?.feature ?? '',
                            isActive : data.isActive,
                            applicationId : data.applicationId,
                            applicationName : data.applicationName,
                            file: File.prototype
                        } as FormData);
                    }
                    else if(data.features?.length === 3) {
                        setHasEnteredExecutiveReportsFeature(true);
                        setFormData({
                            adminFeature: data.features?.find(x=> x.featureTypeId === 0)!.feature,
                            viewFeature: data.features?.find(x=> x.featureTypeId === 1)!.feature,
                            executiveFeature: data.features?.find(x=> x.featureTypeId === 2)!.feature,
                            isActive : data.isActive,
                            applicationId : data.applicationId,
                            applicationName : data.applicationName,
                            file: File.prototype
                        } as FormData);
                    }
                }
            }
        } catch (error) {
            console.error(error);
            showErrorSnackbar(dispatch, `${GetApiErrorCode(error)} - Failed to fetch data`);
        } finally {
            setLoadingData(false);
        }
    };

    useEffectSingleCalls(() => {
        fetchData();
    }, []);

    const setFileContent = (file: File) => {
		setFormData((previousFormData: any) => ({
			...previousFormData,
			file: file
		}));
	};

    const handleFileUpload = (event: any) => {
        const file = event.target.files[0];
        if (file) {
            setFileContent(file);
            setIsValidFileExtension(handleImageExtensionValidation(file.name));
            setFileName(file.name);
        }
    };

    const setFeaturesValue = (executiveFeature: boolean) => {
		if(executiveFeature){
			return [
				{ Id: "0", Feature: formData.adminFeature },
				{ Id: "1", Feature: formData.viewFeature },
				{ Id: "2", Feature: formData.executiveFeature }
			];
		}
		else {
			return [
				{ Id: "0", Feature: formData.adminFeature },
				{ Id: "1", Feature: formData.viewFeature }
			];
		}
	};

    const handleSubmit = async () => {
        const isFormValid = (!isDisplayNameEmpty && isDisplayNameValid && !isAdminFeatureEmpty && 
            !isViewApplicationFeatureEmpty);
		if (!isFormValid) {
			return;
		}
        setCurrentlySubmitting(true);
        try {
            const requestFormData = new FormData();
			const features = setFeaturesValue(hasEnteredExecutiveReportsFeature);
			requestFormData.append("applicationId",  formData.applicationId);
			for (var i = 0; i < features.length; i++) {
				requestFormData.append("features[" + i + "].Id", features[i].Id);
				requestFormData.append("features[" + i + "].Feature", features[i].Feature);
			}
			requestFormData.append("applicationName",  formData.applicationName);
			requestFormData.append("isActive",  JSON.stringify(formData.isActive));
			requestFormData.append("file",  formData.file);

            const response: any = await editRegisteredApplication(requestFormData);
            if (response) {
                showSuccessSnackbar(
                    dispatch,
                    `Application edited successfully`
                );
                dispatch(refreshApplications(true));
                props.onCancelClick(false);
            }
        } catch (e: any) {
            setError(e);
		}
        setCurrentlySubmitting(false);
    };

    return (
        <>
            {loadingData
                ? (<div className="loading-wheel"><LoadIndicator/></div>)
                : <>
                    <ErrorMessage error={error} />
                    <LoadIndicator className='api-loader submitting-loader' visible={currentlySubmitting}/>
                    <div className="form-drawer-content">
                        <div className='form-container form-scroll-horizontal'>
                            <div className="form-info">
                                <div className="form-text-title">Application ID</div>
                                <div className="form-text">{formData.applicationId}</div>
                            </div>
                            <Form
                                formData={formData}
                                readOnly={false}
                                showColonAfterLabel={false}
                                showValidationSummary={false}
                                ref={formRef}
                            >
                                <Item
                                    dataField="applicationName"
                                    editorOptions={applicationDisplayNameOptions}
                                    >
                                    <Label text="Display Name" location="top"/>
                                </Item>
                                <Item
                                    dataField="adminFeature"
                                    editorType="dxSelectBox"
                                    editorOptions={featuresCreationAdminOptions}
                                    cssClass="form-item-spacing"
                                >
                                    <Label text="Admin Feature" location="top"/>
                                </Item>
                                <Item
                                    dataField="viewFeature"
                                    editorType="dxSelectBox"
                                    editorOptions={featuresCreationViewingOptions}
                                >
                                    <Label text="View Application Feature" location="top"/>
                                </Item>
                                <Item
                                    dataField="executiveFeature"
                                    editorType="dxSelectBox"
                                    editorOptions={featuresCreationExecutiveOptions}
                                >
                                    <Label text="Executive Reports Feature (optional)" location="top"/>
                                </Item>
                                <Item
                                    dataField="image"
                                >
                                    <Label text="Logo" location="top"/>
                                    <input type="file" ref={logoRef} accept=".jpg,.jpeg,.png" onChange={handleFileUpload} style={{ display: 'none' }}/>
                                    <button	className="logo-default" onClick={() => logoRef.current?.click()}>Choose File</button>
                                    {fileName && <span className="logo-default file-name">{fileName}</span>}
                                    <div className="logo-default logo-info">
                                        Allowed file extensions: .jpg, .jpeg .png
                                    </div>
                                </Item>
                                <Item
                                    dataField="isActive"
                                    editorType="dxSwitch"
                                    cssClass="dx-form-switch"
                                    editorOptions={visibilityEditorOptions}
                                >
                                    <Label text="Is Active" location="top"/>
                                </Item>
                                <Item>
                                {!isValidFileExtension && 
                                    <div className="invalid-file-text">
                                        Invalid file extension.
                                    </div>
                                }
                                </Item>
                            </Form>
                            <div className="invalid-message invalid-display-name-message-edition">
                            {!isDisplayNameValid && (
                                <span>Display Name can only contain letters, digits, whitespaces, hyphens, underscores and parenthesis up to 100 characters.</span>
                            )}
                            {isDisplayNameEmpty && isDisplayNameValid && (
                                <span>Display Name cannot be empty</span>
                            )}
                            </div>
                            <div className="invalid-message invalid-admin-feature-message-edition">
                            {isAdminFeatureEmpty && (
                                <span>Admin Feature has to be selected</span>
                            )}
                            </div>
                            <div className="invalid-message invalid-view-application-feature-message-edition">
                            {isViewApplicationFeatureEmpty && (
                                <span>View Application Feature has to be selected</span>
                            )}
                            </div>
                        </div>
                        <div className="drawer-footer-buttons">
                            <DrawerFooter handleSubmit={handleSubmit} onCancelClick={() => props.onCancelClick(false)} />
                        </div>
                    </div>
                </>}
        </>
    );
};
export default React.memo(ApplicationEdit);